//frontend/src/components/layouts/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
    MapPin,
    Phone,
    Mail,
    FacebookIcon,
    Instagram,
    LinkedinIcon,
    YoutubeIcon,
    Share2,
    Clock,
    Info,
    HelpCircle,
    FileText,
    MessageSquare,
    Shield,
    RefreshCw,
    Building,
    Twitter
} from 'lucide-react';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { path: '/about', label: 'About Us', icon: <Info className="w-5 h-5" /> },
        { path: '/contact', label: 'Contact Us', icon: <MessageSquare className="w-5 h-5" /> },
        { path: '/faq', label: 'FAQ', icon: <HelpCircle className="w-5 h-5" /> },
        { path: '/privacy-policy', label: 'Privacy Policy', icon: <FileText className="w-5 h-5" /> },
        { path: '/terms', label: 'Terms of Service', icon: <Shield className="w-5 h-5" /> },
        { path: '/refund-policy', label: 'Refund Policy', icon: <RefreshCw className="w-5 h-5" /> }
    ];

    const socialLinks = [
        {
            icon: <FacebookIcon className="w-5 h-5" />,
            url: 'https://www.facebook.com/profile.php?id=61573728508355',
            label: 'Facebook'
        },
        {
            icon: <Instagram className="w-5 h-5" />,
            url: 'https://www.instagram.com/meatbuddy.store/?hl=en',
            label: 'Instagram'
        },
        {
            icon: <Twitter className="w-5 h-5" />,
            url: 'https://x.com/Meat_Buddy',
            label: 'Twitter'
        },
        {
            icon: <LinkedinIcon className="w-5 h-5" />,
            url: 'https://www.linkedin.com/in/meat-buddy-8b4666353/-Linkdin',
            label: 'LinkedIn'
        },
        {
            icon: <YoutubeIcon className="w-5 h-5" />,
            url: 'https://www.youtube.com/channel/UCu4ag1jOQe0ig01I7Z2PhFg',
            label: 'YouTube'
        },
        {
            icon: <Share2 className="w-5 h-5" />,
            url: 'https://pinterest.com/meatbuddy',
            label: 'Pinterest'
        }
    ];

    // Store locations data with Google Maps URLs for Get Directions
    const storeLocations = [
        {
            name: "Store - Seegehalli",
            address: [
                "Shop No 63, Seegehalli,",
                "Bengaluru, Seegehalli,",
                "Karnataka 562130"
            ],
            mapsUrl: "https://maps.app.goo.gl/YeSW9jTzfGvXFz1B7"
        },
        {
            name: "Store - Bhuvaneshwari Nagar",
            address: [
                "2nd Stage, Bhuvaneshwari Nagar,",
                "Nagdevanahalli, Bengaluru,",
                "Karnataka 560056"
            ],
            mapsUrl: "https://maps.app.goo.gl/9uuuP6XrUEV2Pbof9"
        },
        {
            name: "Store - Kengeri",
            address: [
                "15/1, 8th A Cross Rd,",
                "Jnanabharathi Extension, Volagalahalli,",
                "Stage II, Kengeri Satellite Town,",
                "Bengaluru, Karnataka 560060"
            ],
            mapsUrl: "https://maps.app.goo.gl/gPSQtkq6E6QyR2kC7"
        }
    ];

    return (
        <footer className="border-t" style={{
            backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            backgroundPosition: 'center'
        }}>
            <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:py-16 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Company Info */}
                    <div>
                        <h3 className="font-['Crimson_Text'] text-2xl font-bold text-red-600 mb-6 no-underline">
                            Meat Buddy
                        </h3>
                        <div className="space-y-4">
                            {/* Contact Details */}
                            <div className="flex items-center space-x-3">
                                <Phone className="w-5 h-5 text-gray-600" />
                                <a href="tel:+916364749404" className="text-gray-800 hover:text-red-600 transition-colors no-underline">
                                +91 6364749404
                                </a>
                            </div>
                           
                            <div className="flex items-center space-x-3">
                                <Mail className="w-5 h-5 text-gray-600" />
                                <a href="mailto:customersupport@meatbuddy.in" className="text-gray-800 hover:text-red-600 transition-colors no-underline">
                                    customersupport@meatbuddy.in
                                </a>
                            </div>
                            
                            {/* Social Media Links */}
                            <div className="pt-2">
                                <div className="flex space-x-4">
                                    {socialLinks.map((social, index) => (
                                        <a
                                            key={index}
                                            href={social.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={social.label}
                                            className="p-2 rounded-lg bg-gray-100 hover:bg-red-600 hover:text-white text-gray-600 transition-all no-underline"
                                        >
                                            {social.icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Store Locations - Updated with both shops */}
                    <div>
                        <h3 className="font-['Crimson_Text'] text-xl font-semibold text-red-600 mb-4">Store Locations</h3>
                        <div className="space-y-6">
                            {storeLocations.map((store, index) => (
                                <div key={index} className="space-y-3">
                                    <h4 className="font-medium text-gray-900 text-sm">{store.name}</h4>
                                    <div className="flex items-start space-x-3">
                                        <MapPin className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                                        <div className="text-gray-800 text-sm">
                                            {store.address.map((line, lineIndex) => (
                                                <p key={lineIndex}>{line}</p>
                                            ))}
                                            {store.mapsUrl && (
                                                <a
                                                    href={store.mapsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium mt-2 no-underline"
                                                >
                                                    Get Directions
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Store Hours */}
                            <div className="flex items-start space-x-3 pt-2">
                                <Clock className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                                <div className="text-gray-800 text-sm">
                                    <p className="font-medium">Store Hours:</p>
                                    <p>Tuesday - Sunday</p>
                                    <p>7:00 AM - 9:00 PM</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="font-['Crimson_Text'] text-xl font-semibold text-red-600 mb-4">Quick Links</h3>
                        <ul className="grid grid-cols-1 gap-2">
                            {quickLinks.map((link, index) => (
                                <li key={index}>
                                    <Link
                                        to={link.path}
                                        className="flex items-center space-x-2 text-gray-800 hover:text-red-600 transition-colors group no-underline text-sm"
                                    >
                                        <span className="transform group-hover:scale-110 transition-transform duration-200">
                                            {link.icon}
                                        </span>
                                        <span>{link.label}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Copyright */}
            <div className="bg-black">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    {/* Desktop Layout */}
                    <div className="hidden md:flex justify-between items-center text-white text-sm">
                        <p>© {currentYear} Meat Buddy - All Rights Reserved</p>
                        <p>
                            <span className="font-bold">Designed By{' '}
                            <a 
                                href="https://www.hendoi.in" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-white hover:text-red-400 transition-colors underline font-bold"
                            >
                                HENDOI
                            </a></span>
                        </p>
                    </div>
                    
                    {/* Mobile Layout */}
                    <div className="md:hidden text-center text-white text-sm space-y-2">
                        <p>© {currentYear} Meat Buddy - All Rights Reserved</p>
                        <p>
                            <span className="font-bold">Designed By{' '}
                            <a 
                                href="https://www.hendoi.in" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-white hover:text-red-400 transition-colors underline font-bold"
                            >
                                HENDOI
                            </a></span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 768px) {
                    .grid-cols-1 {
                        grid-template-columns: 1fr;
                    }
                   
                    .gap-8 {
                        gap: 2rem;
                    }
                   
                    .space-y-4 > * + * {
                        margin-top: 1rem;
                    }
                   
                    .space-y-6 > * + * {
                        margin-top: 1.5rem;
                    }
                   
                    .px-4 {
                        padding-left: 1rem;
                        padding-right: 1rem;
                    }
                   
                    .py-12 {
                        padding-top: 2rem;
                        padding-bottom: 2rem;
                    }
                }
            `}</style>
        </footer>
    );
};

export default Footer;